import React, { JSX, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { CLIENT_STORAGE_KEY } from 'context/user'
import { FILTERS_STORAGE_KEY } from 'hooks/usePersistentAnalyticsFilters'

export const NO_ACCESS_REASONS = new Set(['no_product_access'])

export const isNoAccess = (error: Error): boolean => {
  const { error: code, error_description: reason } = error as { error?: unknown, error_description?: unknown }
  return code === 'access_denied' && typeof reason === 'string' && NO_ACCESS_REASONS.has(reason)
}

interface SignInProblemPageProps {
  error: Error
}

const buttonClass = 'rounded-md px-4 py-2 text-sm font-medium'

export const SignInProblemPage = ({ error }: SignInProblemPageProps): JSX.Element => {
  const { loginWithRedirect, logout } = useAuth0()
  const noAccess = isNoAccess(error)

  useEffect(() => {
    globalThis.history.replaceState(null, '', globalThis.location.pathname)
  }, [])

  const handleSwitchAccount = (): void => {
    globalThis.localStorage.removeItem(FILTERS_STORAGE_KEY)
    globalThis.localStorage.removeItem(CLIENT_STORAGE_KEY)
    void logout({ logoutParams: { returnTo: globalThis.location.origin } })
  }

  const title = noAccess ? 'No access to StagePilot Analytics' : 'Sign-in did not finish'
  const body = noAccess
    ? 'This account does not have access to Analytics. Analytics is invitation only, so ask your StagePilot contact for an invitation.'
    : 'Something went wrong while signing you in. Please try again in a moment.'

  return (
    <main data-testid='sign-in-problem-page' className='flex min-h-full items-center justify-center px-4'>
      <div className='max-w-md text-center'>
        <div className='flex h-16 items-center justify-center'>
          <img alt='StagePilot Logo' src='/images/logo-letters.png' className='w-auto h-8' />
        </div>
        <h1 data-testid='sign-in-problem-title' className='mt-2 text-lg font-semibold'>
          {title}
        </h1>
        <p data-testid='sign-in-problem-message' className='mt-2 text-sm text-lm-text-secondary dark:text-dm-text-secondary'>
          {body}
        </p>
        <div className='mt-6 flex flex-wrap justify-center gap-3'>
          {!noAccess && (
            <button
              data-testid='sign-in-try-again-button'
              onClick={() => { void loginWithRedirect() }}
              className={`${buttonClass} text-white bg-brand hover:bg-brand/70`}
            >
              Try again
            </button>
          )}
          <button
            data-testid='sign-in-other-account-button'
            onClick={handleSwitchAccount}
            className={`${buttonClass} outline-1 outline-lm-text-accent dark:outline-dm-text-accent hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary`}
          >
            Use a different account
          </button>
        </div>
      </div>
    </main>
  )
}
